import { useState } from "react";
import { format, parse } from "date-fns";
import { tz } from "@date-fns/tz";
import { LuArrowLeft } from "react-icons/lu";
import { Heading, Muted } from "@/components/typography";
import {
  DataTable,
  DataTableContent,
  DataTableFooter,
  DataTablePagination,
  DataTableRangeInfo,
  DataTableSkeleton,
  type ColumnDef,
} from "@/components/data-table";
import { AsyncBoundary } from "@/components/ui/async-boundary";
import { Calendar } from "@/components/ui/calendar";
import { Empty, EmptyDescription, EmptyTitle } from "@/components/ui/empty";
import type { ScannerStat } from "../lib/review-client";
import { useApproverStats, useScannerStats } from "../hooks/use-review";

const SCANNER_COLUMNS: ColumnDef<ScannerStat>[] = [
  { accessorKey: "name", header: "Name" },
  { accessorKey: "pageCount", header: "Pages" },
  { accessorKey: "scriptCount", header: "Scripts" },
];

const APPROVER_COLUMNS: ColumnDef<ScannerStat>[] = [
  { accessorKey: "name", header: "Name" },
  { accessorKey: "scriptCount", header: "Scripts" },
  { accessorKey: "pageCount", header: "Pages" },
];

const SCANNER_SORTING = [{ id: "pageCount", desc: true }];
const APPROVER_SORTING = [{ id: "scriptCount", desc: true }];
const ISO_DAY = "yyyy-MM-dd";
const DISPLAY_DAY = "d MMM yyyy";
const DHAKA = tz("Asia/Dhaka");

function todayISO(): string {
  return format(new Date(), ISO_DAY, { in: DHAKA });
}

function parseISODate(iso: string): Date {
  return parse(iso, ISO_DAY, new Date());
}

function toISODate(date: Date): string {
  return format(date, ISO_DAY);
}

function formatISODate(iso: string): string {
  return format(parseISODate(iso), DISPLAY_DAY);
}

function StatsTable({
  title,
  query,
  columns,
  defaultSorting,
  pageKey,
  sortKey,
  emptyTitle,
  rangeSuffix,
}: {
  title: string;
  query: ReturnType<typeof useScannerStats>;
  columns: ColumnDef<ScannerStat>[];
  defaultSorting: { id: string; desc: boolean }[];
  pageKey: string;
  sortKey: string;
  emptyTitle: string;
  rangeSuffix: string;
}) {
  return (
    <div className="min-w-0 flex-1 space-y-3">
      <Heading as="h2" variant="h4">
        {title}
      </Heading>
      <DataTable
        columns={columns}
        data={query.data ?? []}
        defaultSorting={defaultSorting}
        enableRowSelection={false}
        pageIndexStorageKey={pageKey}
        sortingStorageKey={sortKey}
        tableOptions={{ getRowId: row => row.name }}
      >
        <AsyncBoundary isEmpty={(rows: ScannerStat[]) => rows.length === 0} query={query}>
          <AsyncBoundary.Pending>
            <DataTableSkeleton rows={5} />
          </AsyncBoundary.Pending>
          <AsyncBoundary.Error>
            {(error: unknown) => (
              <DataTableContent
                emptyState={
                  <Empty>
                    <EmptyTitle>Couldn't load stats</EmptyTitle>
                    <EmptyDescription>{String(error)}</EmptyDescription>
                  </Empty>
                }
              />
            )}
          </AsyncBoundary.Error>
          <AsyncBoundary.Empty>
            <DataTableContent
              emptyState={
                <Empty>
                  <EmptyTitle>{emptyTitle}</EmptyTitle>
                  <EmptyDescription>Try a wider date range.</EmptyDescription>
                </Empty>
              }
            />
          </AsyncBoundary.Empty>
          <AsyncBoundary.Success>
            <DataTableContent />
          </AsyncBoundary.Success>
        </AsyncBoundary>
        <DataTableFooter>
          <DataTableRangeInfo suffix={rangeSuffix} />
          <DataTablePagination />
        </DataTableFooter>
      </DataTable>
    </div>
  );
}

export function Leaderboard({ onBack }: { onBack: () => void }) {
  const [range, setRange] = useState(() => {
    const today = parseISODate(todayISO());
    return { from: today, to: today };
  });
  const from = toISODate(range.from);
  const to = toISODate(range.to ?? range.from);
  const scannersQuery = useScannerStats(from, to);
  const approversQuery = useApproverStats(from, to);
  const rangeLabel =
    from === to ? formatISODate(from) : `${formatISODate(from)} – ${formatISODate(to)}`;

  return (
    <div className="p-6 pb-20">
      <div className="mb-4 flex items-center gap-2">
        <button type="button" onClick={onBack} aria-label="Back" className="text-muted-foreground">
          <LuArrowLeft className="size-5" />
        </button>
        <div>
          <Heading as="h1" variant="h4">
            Leaderboard
          </Heading>
          <Muted>{rangeLabel}</Muted>
        </div>
      </div>

      <div className="flex flex-col gap-6 md:flex-row md:items-start">
        <Calendar
          mode="range"
          selected={range}
          defaultMonth={range.from}
          onSelect={next => {
            if (!next?.from) return;
            setRange({ from: next.from, to: next.to ?? next.from });
          }}
        />

        <div className="flex min-w-0 flex-1 flex-col gap-10">
          <StatsTable
            title="Scanners"
            query={scannersQuery}
            columns={SCANNER_COLUMNS}
            defaultSorting={SCANNER_SORTING}
            pageKey="review-leaderboard-page-index"
            sortKey="review-leaderboard-sorting"
            emptyTitle="No uploads in this range"
            rangeSuffix="scanners"
          />
          <StatsTable
            title="Approvers"
            query={approversQuery}
            columns={APPROVER_COLUMNS}
            defaultSorting={APPROVER_SORTING}
            pageKey="review-approver-leaderboard-page-index"
            sortKey="review-approver-leaderboard-sorting"
            emptyTitle="No approvals in this range"
            rangeSuffix="approvers"
          />
        </div>
      </div>
    </div>
  );
}
